'use client'
import { ReactNode, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { doc, onSnapshot } from 'firebase/firestore'
import { firebaseDb } from '@/firebase/init'
import {
  setImageUrl,
  setLoading,
  setError,
} from '@/redux/slices/imageSessionSlice'
import { deleteImageSession } from '@/components/page-partials/quiz/mobile-upload/index'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { trackEventMixpanel } from '@/utils/mixpanel'
import { LESSON_IMAGE_UPLOADED_QR } from '@/utils/mixpanel/eventNames'
import { uploadSourceForPath } from '@/utils/mixpanel/events/upload'
import { ImageSessionState } from '@/types/redux'
import { usePathname } from 'next/navigation'
import authSelector from '@/redux/selectors/auth'
import navigationSelector from '@/redux/selectors/navigation'
import demoLessonSelector from '@/redux/selectors/demo-lesson'
import SvgSpinner from '@/components/icons/spinner'

// Upload sessions already reported, shared by every mounted uploader.
const trackedQrSessionIds = new Set<string>()

export interface Props {
  imagePreviewWrapperClassName?: string
  loader?: ReactNode
}

export default function ImageUploader({
  imagePreviewWrapperClassName = 'image-container w-14 h-14 mb-2 border border-v8-blue rounded-md',
  loader = (
    <div className="w-14 h-14 flex justify-center items-center mb-2 border border-v8-blue rounded-md">
      <SvgSpinner className="w-6 h-6 text-v8-blue" />
    </div>
  ),
}: Props) {
  const [fullscreen, setFullscreen] = useState(false)

  const { error, imageUrl, loading, sessionId } = useSelector(
    ({ imageSession }: { imageSession: ImageSessionState }) => imageSession,
  )
  const pathname = usePathname()
  const { user } = useSelector(authSelector)
  const { selectedLesson } = useSelector(navigationSelector)
  const { currentLesson } = useSelector(demoLessonSelector)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!sessionId) {
      return
    }

    const unsubscribe = onSnapshot(
      doc(firebaseDb, 'uploads', sessionId),
      (doc) => {
        const data = doc.data()
        if (data?.status === 'uploading') {
          dispatch(setLoading(true))
          dispatch(setError(false))
        } else if (data?.status === 'completed') {
          dispatch(setImageUrl(data.imageUrl))
          dispatch(setLoading(false))

          // No event on the demo lesson (home page).
          if (pathname === '/') return

          // The listener is re-created when its deps change and replays the
          // completed session; send the event once per upload session.
          if (trackedQrSessionIds.has(sessionId)) return
          trackedQrSessionIds.add(sessionId)

          trackEventMixpanel(LESSON_IMAGE_UPLOADED_QR, {
            role: user?.role,
            curriculumLevel: user?.curriculumLevel,
            examBoard: user?.examBoard,
            lessonId: selectedLesson?.id,
            source: uploadSourceForPath(pathname),
          })
        } else if (data?.status === 'failed') {
          dispatch(setLoading(false))
          dispatch(setError(true))
        }
      },
    )

    return () => unsubscribe()
  }, [
    sessionId,
    dispatch,
    user,
    selectedLesson?.id,
    pathname,
    currentLesson?.id,
  ])

  const handleDelete = async () => {
    dispatch(setImageUrl(null))

    try {
      if (!sessionId) {
        throw new Error(`Unexpected error, sessionId is ${sessionId}`)
      }

      await deleteImageSession(sessionId)

      dispatch(setError(false))
      dispatch(setLoading(false))
    } catch (err) {
      console.error('Error deleting document: ', err)
      dispatch(setError(true))
    }
  }

  const handleImageClick = () => {
    setFullscreen(true)
  }

  const handleCloseFullscreen = () => {
    setFullscreen(false)
  }

  if (!loading && !error && !imageUrl) {
    return <></>
  }

  return (
    <div className="w-full flex items-center justify-end">
      {loading && loader}
      {error && (
        <div className="text-red-500 mb-2">
          Image upload failed. Please try again.
        </div>
      )}
      {!loading && imageUrl && !error && (
        <div className={imagePreviewWrapperClassName}>
          {/*  eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            className="w-full h-full rounded-md object-cover cursor-pointer"
            alt="Uploaded"
            onClick={handleImageClick}
          />
          <button
            type="button"
            className="delete-button rounded-full border border-v8-ink/25 p-0.5 top-1 right-1 translate-x-1/2 -translate-y-1/2"
            onClick={handleDelete}
          >
            <XMarkIcon className="w-4 h-4" color="#000" aria-hidden="true" />
          </button>
        </div>
      )}
      {fullscreen && (
        <div className="fullscreen-overlay" onClick={handleCloseFullscreen}>
          {/*  eslint-disable-next-line @next/next/no-img-element */}
          <img src={imageUrl ?? undefined} alt="Full screen" />
          <button type="button" className="close-button" onClick={handleCloseFullscreen}>
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}
